export async function saveOrderReference(formAction, value, origin, request = fetch, note) {
  const reference = value.trim();
  if (!reference || reference.length > 50 || /[\u0000-\u001f\u007f]/u.test(reference)) throw new Error('Enter a PO number or job name (50 characters maximum).');
  const url = new URL(formAction, origin);
  if (url.origin !== origin) throw new Error('Invalid cart address.');
  url.pathname = url.pathname.replace(/\/$/, '') + '/update.js';
  url.search = '';
  const response = await request(url.href, {
    method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { 'PO / Job name': reference }, ...(typeof note === 'string' ? { note } : {}) }),
    signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new Error('Your reference could not be saved. Please try Check out again.');
  const cart = await response.json();
  if (cart.attributes?.['PO / Job name'] !== reference || (typeof note === 'string' && (cart.note ?? '') !== note)) {
    throw new Error('Your reference could not be saved. Please try Check out again.');
  }
  return reference;
}

export function requireSavedCartReview(root) {
  const selector = 'button[data-action~="show#addAllToCart"][data-show-go-to-checkout-param="true"]';
  for (const button of root.querySelectorAll(selector)) {
    button.setAttribute('data-show-go-to-checkout-param', 'false');
    button.setAttribute('data-show-should-redirect-param', 'true');
    const restore = root.querySelector('button[data-action~="show#addAllToCart"][data-show-url-params-param]');
    if (restore) button.setAttribute('data-show-url-params-param', restore.getAttribute('data-show-url-params-param'));
    button.textContent = 'Review cart & add PO';
  }
}

if (typeof document !== 'undefined') {
  const cartUrl = new URL('cart', new URL(window.Shopify?.routes?.root || '/', window.location.origin));
  if (/\/a\/saved-carts(?:\/|$)/.test(window.location.pathname)) {
    requireSavedCartReview(document);
    new MutationObserver(() => requireSavedCartReview(document)).observe(document.body, { childList: true, subtree: true });
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('button[data-action~="show#addAllToCart"][data-show-go-to-checkout-param="true"]')) requireSavedCartReview(document);
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || !/\/(?:checkout|checkouts)(?:\/|$)/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(cartUrl.href);
  }, true);
  const saving = new WeakSet();
  const verified = new WeakSet();
  document.addEventListener('submit', async event => {
    const form = event.target;
    const button = event.submitter;
    if (!(form instanceof HTMLFormElement) || button?.name !== 'checkout') return;
    if (verified.delete(form)) return;
    const input = form.elements.namedItem('attributes[PO / Job name]');
    event.preventDefault();
    if (form.id !== 'cart-form' || !(input instanceof HTMLInputElement)) {
      event.stopImmediatePropagation();
      window.location.assign(cartUrl.href);
      return;
    }
    if (!input.value.trim() || input.value.trim().length > 50 || /[\u0000-\u001f\u007f]/u.test(input.value.trim())) {
      input.setCustomValidity('Enter a PO number or job name, up to 50 characters.');
      input.reportValidity();
      input.addEventListener('input', () => input.setCustomValidity(''), { once: true });
      return;
    }
    if (saving.has(form)) return;
    saving.add(form);
    const status = input.closest('.tc-order-reference')?.querySelector('[data-tree-reference-status]');
    const message = text => { if (status) { status.textContent = text; status.hidden = !text; } };
    button.setAttribute('aria-busy', 'true');
    input.readOnly = true;
    const note = form.elements.namedItem('note');
    if (note instanceof HTMLTextAreaElement) note.readOnly = true;
    message('Saving your order reference…');
    try {
      input.value = await saveOrderReference(form.action, input.value, window.location.origin, fetch, note instanceof HTMLTextAreaElement ? note.value : undefined);
      if (!form.isConnected || !button.isConnected || button.disabled) {
        message('Your cart changed. Review it and select Check out again.');
        return;
      }
      input.readOnly = false;
      message('');
      verified.add(form);
      form.requestSubmit(button);
      verified.delete(form);
    } catch {
      message('Your reference could not be saved. Please try Check out again.');
    } finally {
      input.readOnly = false;
      if (note instanceof HTMLTextAreaElement) note.readOnly = false;
      button.removeAttribute('aria-busy');
      saving.delete(form);
    }
  }, true);
}
